import { useMemo, useState } from 'react'
import { AlertCircle, Info } from 'lucide-react'
import { prepareHeatmapData, getOperatorColors, MONTH_NAMES, MONTH_NAMES_SHORT } from '../utils/chartHelpers'

/**
 * Slider component for controlling number of salons
 */
const SalonLimitSlider = ({ value, onChange, max }) => {
  const marks = [20, 50, 100, 150, 200].filter(m => m <= max)
  
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-brand-gray-700">
          Liczba salonów do wyświetlenia: <span className="font-bold">{value}</span>
        </label>
        <span className="text-xs text-brand-gray-500">max: {max}</span>
      </div>
      <input
        type="range"
        min="10"
        max={Math.min(200, max)}
        step="10"
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="w-full h-2 bg-brand-gray-200 rounded-lg appearance-none cursor-pointer accent-brand-blue"
      />
      <div className="flex justify-between mt-1">
        {marks.map((mark) => (
          <span 
            key={mark} 
            className="text-xs text-brand-gray-400"
            style={{ visibility: mark <= max ? 'visible' : 'hidden' }}
          >
            {mark}
          </span>
        ))}
      </div>
    </div>
  )
}

/**
 * Heatmap cell component
 */
const HeatmapCell = ({ value, maxValue, operatorColor, month, salonName }) => {
  const intensity = maxValue > 0 ? value / maxValue : 0
  const colors = getOperatorColors(operatorColor)
  
  // Generate background color based on intensity
  const backgroundColor = value > 0 
    ? `rgba(${hexToRgb(colors.primary)}, ${0.2 + intensity * 0.8})`
    : 'rgba(240, 240, 240, 0.5)'
  
  // Text color based on intensity
  const textColor = intensity > 0.5 ? 'white' : '#374151'
  
  return (
    <div 
      className="w-14 h-6 flex items-center justify-center text-xs font-medium border border-brand-gray-200 cursor-pointer hover:ring-2 hover:ring-brand-blue hover:z-10 transition-all"
      style={{ backgroundColor, color: textColor }}
      title={`${salonName}\n${MONTH_NAMES[month - 1]}: ${value} szt.`}
    >
      {value > 0 ? value : ''}
    </div>
  )
}

/**
 * Helper to convert hex to RGB
 */
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return '0, 0, 0'
  return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
}

/**
 * Monthly Heatmap sub-report component
 */
const MonthlyHeatmap = ({ 
  data,
  operator,
  salonLimit,
  onSalonLimitChange
}) => {
  const [localLimit, setLocalLimit] = useState(salonLimit || 50)

  const heatmapData = useMemo(() => {
    return prepareHeatmapData(data?.salon_data, localLimit)
  }, [data?.salon_data, localLimit])

  // Calculate max value for color intensity
  const maxValue = useMemo(() => {
    if (!heatmapData.length) return 0
    return Math.max(...heatmapData.flatMap(s => 
      [1,2,3,4,5,6,7,8,9,10,11,12].map(m => s[`month_${m}`] || 0)
    ))
  }, [heatmapData])

  const totalSalonsWithSales = data?.salon_data?.filter(s => s.total_sales > 0).length || 0
  const colors = getOperatorColors(operator)

  const handleLimitChange = (newLimit) => {
    setLocalLimit(newLimit)
    if (onSalonLimitChange) {
      onSalonLimitChange(newLimit)
    }
  }

  if (!heatmapData.length) {
    return (
      <div className="bg-white rounded-xl border border-brand-gray-200 shadow-sm p-8">
        <div className="flex flex-col items-center justify-center text-center">
          <AlertCircle className="h-12 w-12 text-amber-500 mb-3" />
          <h3 className="text-lg font-semibold text-brand-gray-800 mb-1">
            Brak danych
          </h3>
          <p className="text-sm text-brand-gray-500">
            Brak danych do wyświetlenia heatmapy
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-brand-gray-200 shadow-sm p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-brand-gray-800">
          Wzorce sprzedaży miesięcznej
        </h3>
        <p className="text-sm text-brand-gray-500 mt-1">
          <span className="font-medium">TOP {heatmapData.length}</span> z {totalSalonsWithSales} salonów z sprzedażą
        </p>
        <p className="text-xs text-brand-gray-400 mt-0.5 italic">
          Sortowane wg: max sprzedaży w miesiącu → liczba aktywnych miesięcy → łączna sprzedaż
        </p>
      </div>

      {/* Slider */}
      <SalonLimitSlider 
        value={localLimit}
        onChange={handleLimitChange}
        max={totalSalonsWithSales}
      />

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-start">
        <Info className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
        <div className="text-xs text-blue-800">
          <p className="font-medium mb-1">Jak czytać heatmapę:</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>Każda komórka = liczba sprzedanych urządzeń w danym miesiącu</li>
            <li>Ciemniejszy kolor = więcej sprzedaży</li>
            <li>Najciekawsze wzorce miesięczne na górze</li>
          </ul>
        </div>
      </div>

      {/* Heatmap */}
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          {/* Header */}
          <div className="flex items-center mb-1">
            <div className="w-48 flex-shrink-0" /> {/* Spacer for salon names */}
            {MONTH_NAMES_SHORT.map((month, index) => (
              <div 
                key={index}
                className="w-14 text-center text-xs font-semibold text-brand-gray-500"
              >
                {month}
              </div>
            ))}
          </div>

          {/* Rows */}
          <div className="space-y-0.5">
            {heatmapData.map((salon, rowIndex) => (
              <div key={rowIndex} className="flex items-center">
                {/* Salon name */}
                <div 
                  className="w-48 flex-shrink-0 text-xs text-brand-gray-700 pr-2 text-right truncate"
                  title={salon.fullSalonName}
                >
                  {salon.salon}
                </div>
                
                {/* Month cells */}
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(month => (
                  <HeatmapCell
                    key={month}
                    value={salon[`month_${month}`] || 0}
                    maxValue={maxValue}
                    operatorColor={operator}
                    month={month}
                    salonName={salon.fullSalonName}
                  />
                ))}
              </div>
            ))}
          </div>

          {/* Legend */}
          <div className="flex items-center mt-4 ml-48 gap-4">
            <span className="text-xs text-brand-gray-500">Intensywność:</span>
            <div className="flex items-center gap-2">
              <div className="w-5 h-4 bg-gray-100 border border-brand-gray-200" />
              <span className="text-xs text-brand-gray-400">0</span>
            </div>
            <div className="flex items-center gap-2">
              <div 
                className="w-5 h-4 border border-brand-gray-200" 
                style={{ backgroundColor: `rgba(${hexToRgb(colors.primary)}, 0.4)` }}
              />
              <span className="text-xs text-brand-gray-400">Niska</span>
            </div>
            <div className="flex items-center gap-2">
              <div 
                className="w-5 h-4 border border-brand-gray-200" 
                style={{ backgroundColor: colors.primary }}
              />
              <span className="text-xs text-brand-gray-400">Wysoka</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MonthlyHeatmap
